/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2019, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

define([
	'./FullScreenAPI',
	'jquery',
	'../../../api/DashboardAPI',
	'../../../app/nls/StringResources',
	'../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'../../../lib/@waca/dashboard-common/dist/api/ContentActionsProviderAPI'
], function(
	FullScreenAPI,
	$,
	DashboardAPI,
	stringResources,
	APIFactory,
	ContentActionsProviderAPI
){
	const FULL_SCREEN_CHANGE_EVENTS = 'webkitfullscreenchange mozfullscreenchange MSFullscreenChange fullscreenchange';

	/**
	 * @implements {FullScreenAPI}
	 */
	class FullScreen {

		static LOGGER_MESSAGE = {
			ENTER: 'enter fullScreen - already in full screen',
			EXIT: 'exit fullScreen - not in full screen',
			IN_PROGRESS: 'change fullScreen state - already in progress'
		}

		constructor({ features }) {
			this._dashboard = features.API;
			this._dashboardState = features.DashboardState;
			this._contentActions = features.ContentActions;
			this._features = features;

			this._logger = this._dashboard.getService(DashboardAPI.GLOBAL_SERVICES.LOGGER);

			this._initialize();
		}

		_initialize() {
			this._api = APIFactory.createAPI(this, [FullScreenAPI, ContentActionsProviderAPI]);
			this._contentActions.registerProvider('fullScreenAction', this._api);
			this._dashboardState.onChangeActive(this._onChangeDashboardState.bind(this));
			this._dashboardState.onUiStateChange(this._onUiStateChange.bind(this));

			this._isEventTriggeredInternally = false;
			this._isFullScreen = false;
			this._inProgress = false;
			this._setupEvents();
		}

		getAPI() {
			return this._api;
		}

		destroy() {
			this._api = null;
			$(document).off(FULL_SCREEN_CHANGE_EVENTS);
		}

		_setupEvents() {
			$(document).on(FULL_SCREEN_CHANGE_EVENTS, this._onFullScreenChangeEvents.bind(this));
		}

		_teardownEvents() {
			$(document).off(FULL_SCREEN_CHANGE_EVENTS);
		}

		/**
		 * @implements {FullScreenAPI#enter}
		 * @description Enter full screen mode.<br/>
		 * Please note that this method is REQUIRED to be invoked through a user
		 * interaction due to security reasons in browsers.<br/>
		 * See the following link for details:<br/>
		 * https://developer.mozilla.org/en-US/docs/Web/API/Element/requestFullScreen
		 */
		enter() {
			if (this.isFullScreen()) {
				this._logger.info(FullScreen.LOGGER_MESSAGE.ENTER);
				return;
			}

			this._onEnterFullScreen();

			if (this._isFullScreenEnabled()) {
				this._isEventTriggeredInternally = true;
				if (document.body.requestFullscreen) {
					document.body.requestFullscreen();
				} else if (document.body.webkitRequestFullscreen) {
					document.body.webkitRequestFullscreen();
				} else if (document.body.mozRequestFullScreen) {
					document.body.mozRequestFullScreen();
				} else if (document.body.msRequestFullscreen && !(window.frameElement && window.frameElement.nodeName === 'IFRAME')) {
					// Don't use full screen mode when we are inside an iframe as
					// IE has a bug where sizes are mis-reported by a factor of
					// 100 in this case. If we are not in an iframe, then full screen is okay.
					//https://connect.microsoft.com/IE/feedback/details/838286
					document.body.msRequestFullscreen();
				}
			}
		}

		exit() {
			if (!this.isFullScreen()) {
				this._logger.info(FullScreen.LOGGER_MESSAGE.EXIT);
				return;
			}

			this._onExitFullScreen();

			if (this._isFullScreenEnabled()) {
				this._isEventTriggeredInternally = true;
				if (document.exitFullscreen) {
					document.exitFullscreen();
				} else if (document.webkitExitFullscreen) {
					document.webkitExitFullscreen();
				} else if (document.mozCancelFullScreen) {
					document.mozCancelFullScreen();
				} else if (document.msExitFullscreen) {
					document.msExitFullscreen();
				}
			}
		}

		isFullScreen() {
			return this._isFullScreen;
		}

		_onChangeDashboardState(data) {
			this._teardownEvents();
			if (!data.info.value) {
				this.exit();
			} else {
				this._setupEvents();
			}
		}

		_onFullScreenChangeEvents() {
			if (this._isEventTriggeredInternally) {
				this._isEventTriggeredInternally = false;
			} else {
				if (this.isFullScreen()) {
					this._onExitFullScreen();
					this._dashboardState.setFullScreen(false);
				} else {
					this._onEnterFullScreen();
				}
			}
		}

		/** Helpers */
		_onEnterFullScreen() {
			// no api to hide the glass yet
			//needs to overwrite glass app/nav bar because layout.css has display:flex !important
			this._activePaneColumn = $('.appview.paneColumn').not('.hidden');
			this._activePaneColumn.find('.appbar').addClass('fullScreenNoGlass');
			this._activePaneColumn.find('.templateBox').addClass('fullScreen');
			$('body').addClass('fullScreen');
			document.body.style.cssText = 'height: 100% !important';
			$('body').append($('<div class="dashboardAriaAlert fullScreenAlert" role="alert"></div>').text(stringResources.get('ariaFullScreenMessage')));

			this._isFullScreen = true;
			this._dashboard.triggerDashboardEvent('enter:fullscreen');
		}

		_onExitFullScreen() {
			//needs to show the glass app & nav bar again.
			this._activePaneColumn.find('.appbar').removeClass('fullScreenNoGlass');
			this._activePaneColumn.find('.templateBox').removeClass('fullScreen');
			$('body').removeClass('fullScreen');
			document.body.style.cssText = '';
			$('body').find('.fullScreenAlert').remove();

			this._isFullScreen = false;
			this._dashboard.triggerDashboardEvent('exit:fullscreen');
		}

		_isFullScreenEnabled() {
			return (document.fullscreenEnabled ||
					document.webkitFullscreenEnabled ||
					document.mozFullScreenEnabled ||
					document.msFullscreenEnabled) ? true : false;
		}

		/**
		 * Callback for DashboardStateAPI#onUiStateChange
		 * @see DashboardStateAPI#onUiStateChange for callback parameters
		 */
		_onUiStateChange({ fullScreen }) {
			if (this._inProgress) {
				this._logger.info(FullScreen.LOGGER_MESSAGE.IN_PROGRESS);
				return;
			}

			this._inProgress = true;
			if (fullScreen === true) {
				this.enter();
			} else {
				this.exit();
			}
			this._inProgress = false;
		}

		/**
		 * @implements ContentActionProviderAPI#getContentActionList
		 */
		getContentActionList() {
			const uiState = this._dashboardState.getUiState();

			let label, icon;
			if (uiState.fullScreen) {
				label = stringResources.get('fullScreenClose');
				icon = this._features['Icons'].getIcon('minimize').id;
			} else {
				label = stringResources.get('fullScreenEnter');
				icon = this._features['Icons'].getIcon('maximize').id;
			}

			return [{
				name: 'FullScreen',
				type: 'Button',
				label,
				icon,
				section: ContentActionsProviderAPI.ACTION_SECTION.DASHBOARD,
				order: 20,
				disabled: uiState.eventGroups,
				action: () => {
					if (uiState.fullScreen) {
						this._dashboardState.setFullScreen(false);
					} else {
						this._dashboardState.setFullScreen(true);
					}
				}
			}];
		}
	}
	return FullScreen;
});
